import {useEffect, useRef, useState} from 'react';
import {useQueryClient} from 'react-query';
import {
	fetchEmployment,
	fetchIdentificationInfo,
	useBindSelfEmploymentPartner,
	useValidateSelfEmployment,
	type EmploymentsResponse,
	type SelfEmploymentProvider,
} from '@rocket-ui/lk-api';
import {useUserInteraction} from '@rocket-ui/user-interaction';
import {
	activateEmploymentWithCurrent,
	buildFullNameFromIdentificationInfo,
	EMPTY_PAYMENT_DETAILS,
	fetchSelfEmploymentStepWithPolling,
	extractInnDigits,
	extractRegistrationBackendFlags,
	getInitialPaymentSubStep,
	getSelfEmploymentAgreementLink,
	getSelfEmploymentErrorResumeStep,
	getSelfEmploymentProfilesFirstStatus,
	getSelfEmploymentProvider,
	getSelfEmploymentSnapshot,
	hasSelfEmploymentAgreementLink,
	getStatusConnectionNextAction,
	getStatusConnectionPaymentMethods,
	hasIdentificationNameChanged,
	isFinanceSelfEmploymentFlow,
	isRegistrationStepVisible,
	isSelfEmploymentAgreementSent,
	isSelfEmploymentAgreementSigned,
	isSelfEmploymentBindRequest,
	isSelfEmploymentFnsPending,
	isSelfEmploymentSignedNotSelfEmployed,
	markAlfaAgreementFrameOpened,
	isWorkStatusErrorState,
	mapEmploymentsToWorkStatusConnections,
	mapSelfEmploymentProfileConnectionState,
	markWorkStatusAsCurrent,
	RegistrationStep,
	RegistrationWorkStatus,
	readSavedSnilsDigits,
	requiresMyTaxConnectionFlow,
	resolveRegistrationFlow,
	shouldRequestSnilsStep,
	skipsStatusConnectionInn,
	resolveEmploymentRequestProvider,
	resolveSelfEmploymentIntroAction,
	resolveSelfEmploymentStep,
	type SelfEmploymentIntroAction,
	switchCurrentEmployment,
	WORK_STATUS_STEP_ENABLED,
	type SelfEmploymentCheckOptions,
} from 'features/registration';
import {getRegistrationApiErrorMessage} from './helpers';
import type {RegistrationDerived} from './useRegistrationDerived';
import type {RegistrationWizardState} from './useRegistrationWizardState';

/** Опрос GET /employment, пока анкета Альфа не подписана (iframe или «Секундочку..»). */
const PARTNER_AGREEMENT_POLL_INTERVAL_MS = 7_000;
const PARTNER_AGREEMENT_POLL_ATTEMPTS = 10;

/** После «Доступ предоставлен» (Альфа): опрос прав у ФНС. */
const ALFA_PERMISSIONS_POLL_INTERVAL_MS = 10_000;
const ALFA_PERMISSIONS_POLL_ATTEMPTS = 3;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** ИНН уже лежит в профиле — для `free_smz` «Подключить» может сразу звать bind_partner. */
const fetchSavedInnDigits = async (): Promise<string | null> => {
	try {
		const {data} = await fetchIdentificationInfo();
		const info = data?.info && typeof data.info === 'object' ? data.info : null;
		const inn = info && 'inn' in info && typeof info.inn === 'string' ? extractInnDigits(info.inn) : '';

		return inn || null;
	} catch {
		return null;
	}
};

/**
 * Терминальный шаг, пока открыт iframe соглашения.
 * `null` — соглашение ещё не signed, фрейм не закрываем.
 * После подписи: ФНС / доступ в «Мой налог» / не самозанятый / успех.
 */
const resolvePartnerAgreementPollStep = (
	data: EmploymentsResponse,
	provider: SelfEmploymentProvider,
): RegistrationStep | null => {
	if (!isSelfEmploymentAgreementSigned(data, provider)) {
		return null;
	}

	if (isSelfEmploymentFnsPending(data, provider)) {
		return RegistrationStep.MyTaxFnsPending;
	}

	if (isSelfEmploymentBindRequest(data, provider)) {
		return provider === 'free_smz' ? RegistrationStep.MyTaxFnsPending : RegistrationStep.MyTaxAccess;
	}

	if (isSelfEmploymentSignedNotSelfEmployed(data, provider)) {
		return RegistrationStep.MyTaxNotSelfEmployed;
	}

	const snapshot = getSelfEmploymentSnapshot(data, provider);

	if (snapshot?.status === 'validated' || snapshot?.validated) {
		return RegistrationStep.MyTaxSuccess;
	}

	if (snapshot?.status === 'need_permissions' || snapshot?.error === 'need_permissions') {
		return provider === 'free_smz' ? RegistrationStep.MyTaxFnsPending : RegistrationStep.MyTaxAccess;
	}

	return resolveSelfEmploymentStep(snapshot, {mode: 'status'});
};

type Params = {
	state: RegistrationWizardState;
	postInnFlow: RegistrationDerived['postInnFlow'];
	handleClose: () => void;
};

/** Подключение статуса: переходы «Мой налог», привязка партнёра и активация статуса. */
export const useStatusConnectionFlow = ({state, postInnFlow, handleClose}: Params) => {
	const {notifyUser} = useUserInteraction();
	const {mutateAsync: validateSelfEmployment} = useValidateSelfEmployment();
	const {mutateAsync: bindSelfEmploymentPartner} = useBindSelfEmploymentPartner();
	const [isAlfaStatusConfirmOpen, setIsAlfaStatusConfirmOpen] = useState(false);
	/** Провайдер, к которому партнёр уже привязан в этой сессии: повторный bind_partner бэк отклоняет. */
	const boundPartnerProviderRef = useRef<SelfEmploymentProvider | null>(null);
	/** Ссылка на анкету партнёра из ответа bind_partner — открывается в iframe поверх визарда. */
	const [partnerAgreementLink, setPartnerAgreementLink] = useState<string | null>(null);
	/** Сохраняем ссылку, чтобы после закрытия крестиком «Приступить» снова открыл анкету без повторного bind. */
	const agreementLinkRef = useRef<string | null>(null);
	/** Анкету открыли с экрана статусов — закрытие крестиком возвращает на статусы, а не в опрос. */
	const openedAgreementFromWorkStatusRef = useRef(false);
	const queryClient = useQueryClient();

	const {
		hasInnSaved,
		innStep,
		myTaxCheckingMode,
		myTaxCheckingOptions,
		myTaxLoadErrorRetry,
		personalInfo,
		registrationFlow,
		resetPaymentFlow,
		setIsSubmitting,
		setMyTaxCheckingMode,
		setMyTaxCheckingOptions,
		setMyTaxFooterLoading,
		setMyTaxLoadErrorRetry,
		setMyTaxNameMismatch,
		setPaymentDetails,
		setPaymentSubStep,
		setPendingPaymentMethod,
		setBackendFlags,
		setHasInnSaved,
		setHasSnilsSaved,
		setInnStep,
		setSnilsStep,
		setStatusConnectionIncludesInn,
		setStatusConnectionIncludesSnils,
		selfEmploymentPartner,
		setSelfEmploymentPartner,
		setStep,
		setWorkStatus,
		setWorkStatusConnections,
		snilsStep,
		step,
		workStatus,
		workStatusConnections,
		hasSnilsSaved,
	} = state;

	const partnerProvider: SelfEmploymentProvider = selfEmploymentPartner ?? getSelfEmploymentProvider();
	/** Анкета Альфа. `free_smz` и rocketwork идут своим флоу. */
	const alfaPartnerFlow =
		selfEmploymentPartner === 'alfa' ||
		(selfEmploymentPartner !== 'rocketwork' &&
			selfEmploymentPartner !== 'free_smz' &&
			isFinanceSelfEmploymentFlow(workStatus));

	const connectEmploymentStatusAndShowSuccess = async () => {
		if (!workStatus) {
			return;
		}

		setIsSubmitting(true);

		try {
			await activateEmploymentWithCurrent(workStatus, {
				provider: resolveEmploymentRequestProvider(workStatus, selfEmploymentPartner),
			});
			setStep(RegistrationStep.MyTaxSuccess);
		} catch {
			setMyTaxLoadErrorRetry('activate');
			setStep(RegistrationStep.MyTaxLoadError);
		} finally {
			setIsSubmitting(false);
		}
	};

	const startStatusConnectionPayment = () => {
		const methods = getStatusConnectionPaymentMethods(workStatus, registrationFlow.paymentMethods);

		if (methods.length === 0) {
			void connectEmploymentStatusAndShowSuccess();
			return;
		}

		const method = methods[0];
		setPendingPaymentMethod(method);
		setPaymentSubStep(getInitialPaymentSubStep(method));
		setPaymentDetails(EMPTY_PAYMENT_DETAILS);
		setStep(RegistrationStep.PaymentMethod);
	};

	const goAfterStatusDocuments = () => {
		const nextAction = getStatusConnectionNextAction(workStatus, registrationFlow);

		if (nextAction.type === 'my_tax_access') {
			void startSelfEmploymentStatusCheck();
			return;
		}

		if (nextAction.type === 'payment') {
			startStatusConnectionPayment();
			return;
		}

		void connectEmploymentStatusAndShowSuccess();
	};

	const completeSelfEmploymentValidation = async () => {
		setIsSubmitting(true);

		try {
			const {data} = await fetchIdentificationInfo();
			const info = data?.info;

			if (info && typeof info === 'object' && hasIdentificationNameChanged(personalInfo, info)) {
				setMyTaxNameMismatch({
					inn: innStep.inn,
					fullName: buildFullNameFromIdentificationInfo(info),
				});
				setStep(RegistrationStep.MyTaxNameMismatch);
				return;
			}
		} catch {
			setMyTaxLoadErrorRetry('activate');
			setStep(RegistrationStep.MyTaxLoadError);
			return;
		} finally {
			setIsSubmitting(false);
		}

		await connectEmploymentStatusAndShowSuccess();
	};

	const goAfterTaxStep = async () => {
		// Флаг и СНИЛС читаем заново: setState из refresh ещё не попал в этот рендер.
		let snilsRequirement = registrationFlow.snils;
		let savedSnils = hasSnilsSaved ? snilsStep.snils : null;

		try {
			const {data} = await fetchIdentificationInfo();
			const flags = extractRegistrationBackendFlags(data);

			setBackendFlags(flags);
			snilsRequirement = resolveRegistrationFlow({
				citizenshipOksmCode: personalInfo.citizenshipOksmCode,
				residenceOksmCode: personalInfo.residenceOksmCode,
				workStatus,
				backendFlags: flags,
			}).snils;
			savedSnils = readSavedSnilsDigits(data?.info);

			if (savedSnils) {
				setHasSnilsSaved(true);
				setSnilsStep({snils: savedSnils});
			}
		} catch {
			// Сеть недоступна — решаем по уже известным флагу и СНИЛСу.
		}

		if (shouldRequestSnilsStep(snilsRequirement, savedSnils)) {
			setStatusConnectionIncludesSnils(true);
			setStep(RegistrationStep.Snils);
			return;
		}

		setStatusConnectionIncludesSnils(false);
		goAfterSnilsStep();
	};

	const goAfterSnilsStep = () => {
		if (isRegistrationStepVisible(registrationFlow.residentCode)) {
			setStep(RegistrationStep.ResidentCode);
			return;
		}

		goAfterStatusDocuments();
	};

	const leaveStatusConnectionSuccess = () => {
		setWorkStatus(null);
		setStatusConnectionIncludesInn(true);
		setStatusConnectionIncludesSnils(true);

		// После успешного подключения всегда возвращаемся на экран выбора статуса
		// (с заголовком «Подключим ещё?»). Уйти из регистрации можно кнопкой «Продолжить».
		if (WORK_STATUS_STEP_ENABLED) {
			setStep(RegistrationStep.WorkStatus);
		} else {
			handleClose();
		}
	};

	const completeStatusConnection = () => {
		if (!workStatus) {
			if (WORK_STATUS_STEP_ENABLED) {
				setStep(RegistrationStep.WorkStatus);
			} else {
				handleClose();
			}
			return;
		}

		const status = workStatus;

		if (status !== 'self_employment') {
			setWorkStatusConnections((current) => markWorkStatusAsCurrent(current, status));
			leaveStatusConnectionSuccess();
			return;
		}

		// СМЗ: «Продолжить» всегда делает статус основным, даже если current уже был.
		void (async () => {
			setIsSubmitting(true);
			setWorkStatusConnections((current) => markWorkStatusAsCurrent(current, status));

			try {
				const data = await switchCurrentEmployment(status, {
					provider: resolveEmploymentRequestProvider(status, selfEmploymentPartner),
				});
				setWorkStatusConnections(mapEmploymentsToWorkStatusConnections(data));
				leaveStatusConnectionSuccess();
			} catch (error) {
				try {
					const {data} = await fetchEmployment();
					setWorkStatusConnections(mapEmploymentsToWorkStatusConnections(data));
				} catch {
					/* ignore */
				}

				notifyUser(getRegistrationApiErrorMessage(error, 'Не удалось активировать статус'));
			} finally {
				setIsSubmitting(false);
			}
		})();
	};

	/** Возврат на экран выбора статуса при выходе из подключения статуса. */
	const exitStatusConnectionToWorkStatus = () => {
		setIsAlfaStatusConfirmOpen(false);
		setPartnerAgreementLink(null);
		resetPaymentFlow();
		setWorkStatus(null);
		setStatusConnectionIncludesInn(true);
		setStatusConnectionIncludesSnils(true);

		if (WORK_STATUS_STEP_ENABLED) {
			setStep(RegistrationStep.WorkStatus);
		} else {
			handleClose();
		}
	};

	const handleWorkStatusSelect = (status: RegistrationWorkStatus, provider?: SelfEmploymentProvider) => {
		const resolvedProvider = status === 'self_employment' ? provider ?? getSelfEmploymentProvider() : undefined;

		setSelfEmploymentPartner(resolvedProvider ?? null);

		const employment = queryClient.getQueryData<EmploymentsResponse>(['employment']);
		const profileState = resolvedProvider
			? mapSelfEmploymentProfileConnectionState(
					employment?.selfEmploymentProfiles?.find((item) => item.provider === resolvedProvider),
				) ?? 'available'
			: undefined;
		const connectionState = profileState ?? workStatusConnections[status] ?? 'available';

		// Жёлтый с галочкой (current) или ожидание — ничего.
		if (connectionState === 'current' || connectionState === 'pending' || connectionState === 'pending_alfa') {
			return;
		}

		// Жёлтый без галочки — PUT /employment, сделать основным (один запрос).
		if (connectionState === 'connected') {
			void (async () => {
				setIsSubmitting(true);
				setWorkStatusConnections((current) => markWorkStatusAsCurrent(current, status));

				try {
					const data = await switchCurrentEmployment(status, {
						provider: resolveEmploymentRequestProvider(status, selfEmploymentPartner),
					});
					setWorkStatusConnections(mapEmploymentsToWorkStatusConnections(data));
				} catch (error) {
					try {
						const {data} = await fetchEmployment();
						setWorkStatusConnections(mapEmploymentsToWorkStatusConnections(data));
					} catch {
						/* ignore */
					}

					notifyUser(getRegistrationApiErrorMessage(error, 'Не удалось активировать статус'));
				} finally {
					setIsSubmitting(false);
				}
			})();

			return;
		}

		// Красный СМЗ — флоу по типу ошибки.
		if (status === 'self_employment' && isWorkStatusErrorState(connectionState)) {
			const resumeStep = getSelfEmploymentErrorResumeStep(connectionState);

			setWorkStatus(status);
			setStep(resumeStep ?? RegistrationStep.MyTaxIntro);

			return;
		}

		setWorkStatus(status);
		setStep(RegistrationStep.MyTaxIntro);
	};

	/** Плитка free_smz: экран «Подключить?» с ИНН, bind_partner с provider free_smz. */
	const handleFreeSmzSelect = () => {
		setSelfEmploymentPartner('free_smz');
		setWorkStatus('self_employment');
		setStep(RegistrationStep.MyTaxIntro);
	};

	const runSelfEmploymentCheck = async ({
		mode,
		skipValidation = false,
	}: SelfEmploymentCheckOptions) => {
		setMyTaxCheckingMode(mode);
		setMyTaxCheckingOptions({skipValidation});
		setIsSubmitting(true);
		setStep(RegistrationStep.MyTaxChecking);

		try {
			if (!skipValidation) {
				await validateSelfEmployment();
			}

			const nextStep = await fetchSelfEmploymentStepWithPolling({mode, provider: partnerProvider});

			if (nextStep === RegistrationStep.MyTaxSuccess) {
				await completeSelfEmploymentValidation();
				return;
			}

			if (nextStep === RegistrationStep.WorkStatus) {
				agreementLinkRef.current = null;
				setWorkStatus(null);

				if (WORK_STATUS_STEP_ENABLED) {
					setStep(RegistrationStep.WorkStatus);
				} else {
					handleClose();
				}

				return;
			}

			setStep(nextStep);
		} catch {
			setMyTaxLoadErrorRetry('check');
			setStep(RegistrationStep.MyTaxLoadError);
		} finally {
			setIsSubmitting(false);
		}
	};

	const startSelfEmploymentStatusCheck = () => runSelfEmploymentCheck({mode: 'status'});

	const bindSelfEmploymentPartnerAfterInn = async (): Promise<boolean> => {
		const provider = partnerProvider;

		if (boundPartnerProviderRef.current === provider) {
			return true;
		}

		try {
			await bindSelfEmploymentPartner({provider});
			boundPartnerProviderRef.current = provider;
			return true;
		} catch {
			if (provider === 'free_smz') {
				try {
					const {data} = await fetchEmployment();
					const freeSmzProfile = data.selfEmploymentProfiles?.find((item) => item.provider === 'free_smz');

					if (!freeSmzProfile) {
						setMyTaxLoadErrorRetry('intro');
						setStep(RegistrationStep.MyTaxLoadError);
						return false;
					}

					boundPartnerProviderRef.current = provider;
					return true;
				} catch {
					setMyTaxLoadErrorRetry('intro');
					setStep(RegistrationStep.MyTaxLoadError);
					return false;
				}
			}

			setStep(RegistrationStep.MyTaxAccess);
			return false;
		}
	};

	const goToSelfEmploymentCheckInnPath = () => {
		setStatusConnectionIncludesInn(true);
		setStatusConnectionIncludesSnils(
			isRegistrationStepVisible(registrationFlow.snils) && !hasSnilsSaved,
		);
		setStep(RegistrationStep.Inn);
	};

	const proceedFromMyTaxIntro = async () => {
		if (!requiresMyTaxConnectionFlow(workStatus)) {
			if (hasInnSaved || skipsStatusConnectionInn(workStatus, registrationFlow)) {
				setStatusConnectionIncludesInn(false);
				await goAfterTaxStep();
				return;
			}

			goToSelfEmploymentCheckInnPath();
			return;
		}

		setIsSubmitting(true);

		try {
			const {data} = await fetchEmployment();
			const freeSmzProfile = data.selfEmploymentProfiles?.find((item) => item.provider === 'free_smz');
			const snapshot = getSelfEmploymentSnapshot(data, partnerProvider);
			// Пустой selfEmploymentProfiles и нет профиля free_smz: старт флоу free_smz.
			const resolvedAction = resolveSelfEmploymentIntroAction(snapshot);
			const action: SelfEmploymentIntroAction =
				partnerProvider === 'free_smz' && (!freeSmzProfile || resolvedAction.type === 'no_permissions')
					? {type: 'proceed_to_inn'}
					: resolvedAction;

			switch (action.type) {
				case 'proceed_to_inn': {
					let innSaved = hasInnSaved;

					if (!innSaved && partnerProvider === 'free_smz') {
						const inn = await fetchSavedInnDigits();

						if (inn) {
							setHasInnSaved(true);
							setInnStep({inn});
							innSaved = true;
						}
					}

					if (innSaved) {
						setStatusConnectionIncludesInn(false);
						const bound = await bindSelfEmploymentPartnerAfterInn();

						if (!bound) {
							return;
						}

						await goAfterTaxStep();
						return;
					}

					goToSelfEmploymentCheckInnPath();
					return;
				}
				case 'check_inn_or_wait_fns':
					await runSelfEmploymentCheck({mode: 'status', skipValidation: true});
					return;
				case 'not_selfemployed':
					setStep(RegistrationStep.MyTaxNotSelfEmployed);
					return;
				case 'no_permissions':
					setStep(RegistrationStep.MyTaxAccess);
					return;
				case 'fns_pending':
					setStep(RegistrationStep.MyTaxFnsPending);
					return;
				case 'sign_agreement': {
					const link = getSelfEmploymentAgreementLink(data, partnerProvider);

					if (link) {
						markAlfaAgreementFrameOpened(partnerProvider);
						agreementLinkRef.current = link;
						setPartnerAgreementLink(link);
						return;
					}

					setWorkStatus(null);

					if (WORK_STATUS_STEP_ENABLED) {
						setStep(RegistrationStep.WorkStatus);
					} else {
						handleClose();
					}

					return;
				}
				case 'load_error':
					setMyTaxLoadErrorRetry('intro');
					setStep(RegistrationStep.MyTaxLoadError);
					return;
			}
		} catch {
			setMyTaxLoadErrorRetry('intro');
			setStep(RegistrationStep.MyTaxLoadError);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleAlfaStatusConfirm = () => {
		setIsAlfaStatusConfirmOpen(false);
		void (async () => {
			setIsSubmitting(true);
			/** Последний employment/bind — чтобы понять signed без лишнего запроса. */
			let latestProfiles: Parameters<typeof isSelfEmploymentAgreementSigned>[0] = null;

			try {
				// Сначала employment: если в profiles уже есть agreement.link — bind_partner не вызываем.
				if (!agreementLinkRef.current) {
					try {
						const {data} = await fetchEmployment();
						latestProfiles = data;
						const existingLink = getSelfEmploymentAgreementLink(data, partnerProvider);

						if (existingLink) {
							boundPartnerProviderRef.current = partnerProvider;
							agreementLinkRef.current = existingLink;
						} else if (
							hasSelfEmploymentAgreementLink(data, partnerProvider) ||
							isSelfEmploymentAgreementSigned(data, partnerProvider)
						) {
							// link есть, но уже signed (или не для iframe) — партнёр уже привязан.
							boundPartnerProviderRef.current = partnerProvider;
						}
					} catch {
						/* без employment — привязываем партнёра как обычно */
					}
				}

				if (!agreementLinkRef.current && boundPartnerProviderRef.current !== partnerProvider) {
					const response = await bindSelfEmploymentPartner({provider: partnerProvider});
					boundPartnerProviderRef.current = partnerProvider;
					latestProfiles = response;
					agreementLinkRef.current = getSelfEmploymentAgreementLink(response, partnerProvider);
				}
			} catch {
				/* partner bind may already exist */
			} finally {
				setIsSubmitting(false);
			}

			if (agreementLinkRef.current) {
				openedAgreementFromWorkStatusRef.current = false;
				markAlfaAgreementFrameOpened(partnerProvider);
				setPartnerAgreementLink(agreementLinkRef.current);
				return;
			}

			agreementLinkRef.current = null;

			if (isSelfEmploymentAgreementSent(latestProfiles, partnerProvider)) {
				setWorkStatus(null);

				if (WORK_STATUS_STEP_ENABLED) {
					setStep(RegistrationStep.WorkStatus);
				} else {
					handleClose();
				}

				return;
			}

			if (latestProfiles) {
				const nextStep = resolveSelfEmploymentStep(getSelfEmploymentSnapshot(latestProfiles, partnerProvider), {
					mode: 'status',
				});

				if (nextStep === RegistrationStep.MyTaxSuccess) {
					await completeSelfEmploymentValidation();
					return;
				}

				if (nextStep === RegistrationStep.WorkStatus) {
					setWorkStatus(null);

					if (WORK_STATUS_STEP_ENABLED) {
						setStep(RegistrationStep.WorkStatus);
					} else {
						handleClose();
					}

					return;
				}

				setStep(nextStep);
				return;
			}

			setStep(RegistrationStep.PartnerAgreementChecking);
		})();
	};

	const handleAlfaStatusConfirmCancel = () => {
		setIsAlfaStatusConfirmOpen(false);
	};

	const handleMyTaxIntroProceed = () => {
		// Альфа «Приступим?»: «Продолжить» сразу запускает bind/анкету (раньше — кнопка на модалке).
		if (alfaPartnerFlow && postInnFlow.intro.confirm) {
			handleAlfaStatusConfirm();
			return;
		}

		void proceedFromMyTaxIntro();
	};

	/** «Узнать больше» — модалка «Для работы требуется…»; иначе drawer «Как стать…». */
	const handleMyTaxIntroSecondary = () => {
		if (postInnFlow.intro.secondaryAction?.kind === 'confirmInfo' && postInnFlow.intro.confirm) {
			setIsAlfaStatusConfirmOpen(true);
		}
	};

	const openPartnerAgreement = (url: string, provider?: SelfEmploymentProvider) => {
		const resolved = provider ?? partnerProvider;

		setSelfEmploymentPartner(resolved === 'free_smz' ? 'free_smz' : null);
		markAlfaAgreementFrameOpened(resolved);
		openedAgreementFromWorkStatusRef.current = true;
		agreementLinkRef.current = url;
		setWorkStatus('self_employment');
		setPartnerAgreementLink(url);
	};

	const handlePartnerAgreementClose = () => {
		const returnToStatuses = openedAgreementFromWorkStatusRef.current;
		openedAgreementFromWorkStatusRef.current = false;
		setPartnerAgreementLink(null);

		if (returnToStatuses) {
			agreementLinkRef.current = null;
			void queryClient.invalidateQueries(['employment']);

			if (WORK_STATUS_STEP_ENABLED) {
				setStep(RegistrationStep.WorkStatus);
			}

			return;
		}

		setStep(RegistrationStep.PartnerAgreementChecking);
	};

	const handleMyTaxLoadErrorRetry = () => {
		if (myTaxLoadErrorRetry === 'partner_agreement') {
			agreementLinkRef.current = null;
			setStep(RegistrationStep.MyTaxIntro);
			return;
		}

		if (myTaxLoadErrorRetry === 'intro') {
			void proceedFromMyTaxIntro();
			return;
		}

		if (myTaxLoadErrorRetry === 'activate') {
			void connectEmploymentStatusAndShowSuccess();
			return;
		}

		if (myTaxLoadErrorRetry === 'my_tax') {
			if (step === RegistrationStep.MyTaxLoadError) {
				setStep(
					myTaxCheckingMode === 'permissions'
						? RegistrationStep.MyTaxAccessError
						: RegistrationStep.MyTaxAccess,
				);
			}

			void handleGoToMyTax();
			return;
		}

		void runSelfEmploymentCheck({mode: myTaxCheckingMode, ...myTaxCheckingOptions});
	};

	const handleGoToMyTax = async () => {
		const url = postInnFlow.access?.primaryAction.url;

		if (!url) {
			return;
		}

		setMyTaxFooterLoading('goToSite');
		setIsSubmitting(true);

		try {
			// Обычный СМЗ: validation создаёт запрос прав в «Мой налог». Альфа — только открываем сайт.
			if (!alfaPartnerFlow) {
				await validateSelfEmployment();
			}

			window.open(url, '_blank', 'noopener,noreferrer');
		} catch {
			setMyTaxLoadErrorRetry('my_tax');
			setStep(RegistrationStep.MyTaxLoadError);
		} finally {
			setIsSubmitting(false);
			setMyTaxFooterLoading(null);
		}
	};

	/**
	 * Альфа: «Доступ предоставлен» → «Секундочку..» (права у ФНС),
	 * только poll GET /employment (без POST validation), до 3 раз с интервалом 10 с.
	 */
	const runAlfaPermissionsCheck = async () => {
		setMyTaxCheckingMode('permissions');
		setIsSubmitting(true);
		setStep(RegistrationStep.MyTaxChecking);

		try {
			let lastData: Awaited<ReturnType<typeof fetchEmployment>>['data'] | null = null;

			for (let attempt = 1; attempt <= ALFA_PERMISSIONS_POLL_ATTEMPTS; attempt += 1) {
				const {data} = await fetchEmployment();
				lastData = data;
				queryClient.setQueryData(['employment'], data);

				const profileStatus = getSelfEmploymentProfilesFirstStatus(data, partnerProvider);

				if (isSelfEmploymentAgreementSent(data, partnerProvider)) {
					const link = getSelfEmploymentAgreementLink(data, partnerProvider);

					if (link) {
						markAlfaAgreementFrameOpened(partnerProvider);
						agreementLinkRef.current = link;
						setPartnerAgreementLink(link);
						return;
					}

					setWorkStatus(null);

					if (WORK_STATUS_STEP_ENABLED) {
						setStep(RegistrationStep.WorkStatus);
					} else {
						handleClose();
					}

					return;
				}

				if (isSelfEmploymentFnsPending(data, partnerProvider)) {
					setStep(RegistrationStep.MyTaxFnsPending);
					return;
				}

				if (isSelfEmploymentBindRequest(data, partnerProvider) || profileStatus === 'pending') {
					setStep(RegistrationStep.MyTaxAccess);
					return;
				}

				if (profileStatus === 'validated') {
					await completeSelfEmploymentValidation();
					return;
				}

				// need_permissions / unknown — ещё нет итога, продолжаем опрос.
				if (profileStatus === 'need_permissions' || profileStatus === 'unknown') {
					if (attempt >= ALFA_PERMISSIONS_POLL_ATTEMPTS) {
						setStep(
							profileStatus === 'need_permissions'
								? RegistrationStep.MyTaxAccessError
								: RegistrationStep.MyTaxFnsPending,
						);
						return;
					}

					await sleep(ALFA_PERMISSIONS_POLL_INTERVAL_MS);
					continue;
				}

				const nextStep = resolveSelfEmploymentStep(getSelfEmploymentSnapshot(data, partnerProvider), {
					mode: 'permissions',
				});

				if (nextStep === RegistrationStep.MyTaxSuccess) {
					await completeSelfEmploymentValidation();
					return;
				}

				setStep(nextStep);
				return;
			}

			const lastStatus = lastData ? getSelfEmploymentProfilesFirstStatus(lastData, partnerProvider) : null;

			if (lastData && isSelfEmploymentAgreementSent(lastData, partnerProvider)) {
				const link = getSelfEmploymentAgreementLink(lastData, partnerProvider);

				if (link) {
					markAlfaAgreementFrameOpened(partnerProvider);
					agreementLinkRef.current = link;
					setPartnerAgreementLink(link);
					return;
				}

				setWorkStatus(null);

				if (WORK_STATUS_STEP_ENABLED) {
					setStep(RegistrationStep.WorkStatus);
				} else {
					handleClose();
				}

				return;
			}

			if (lastData && isSelfEmploymentFnsPending(lastData, partnerProvider)) {
				setStep(RegistrationStep.MyTaxFnsPending);
				return;
			}

			if (lastData && (isSelfEmploymentBindRequest(lastData, partnerProvider) || lastStatus === 'pending')) {
				setStep(RegistrationStep.MyTaxAccess);
				return;
			}

			if (lastStatus === 'validated') {
				await completeSelfEmploymentValidation();
				return;
			}

			if (lastStatus === 'need_permissions') {
				setStep(RegistrationStep.MyTaxAccessError);
				return;
			}

			if (lastStatus === 'unknown') {
				setStep(RegistrationStep.MyTaxFnsPending);
				return;
			}

			if (!lastData) {
				setMyTaxLoadErrorRetry('check');
				setStep(RegistrationStep.MyTaxLoadError);
				return;
			}

			const fallbackStep = resolveSelfEmploymentStep(getSelfEmploymentSnapshot(lastData, partnerProvider), {
				mode: 'permissions',
			});

			if (fallbackStep === RegistrationStep.MyTaxSuccess) {
				await completeSelfEmploymentValidation();
				return;
			}

			setStep(fallbackStep);
		} catch {
			setMyTaxLoadErrorRetry('check');
			setStep(RegistrationStep.MyTaxLoadError);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleAccessGranted = () => {
		if (!requiresMyTaxConnectionFlow(workStatus)) {
			return;
		}

		setMyTaxFooterLoading('confirm');

		if (alfaPartnerFlow) {
			void runAlfaPermissionsCheck().finally(() => setMyTaxFooterLoading(null));
			return;
		}

		void runSelfEmploymentCheck({mode: 'permissions'}).finally(() => setMyTaxFooterLoading(null));
	};

	const handleMyTaxNameMismatchConfirm = () => {
		void connectEmploymentStatusAndShowSuccess();
	};

	const handleMyTaxAccessErrorRetry = () => {
		setMyTaxFooterLoading('confirm');

		if (alfaPartnerFlow) {
			void runAlfaPermissionsCheck().finally(() => setMyTaxFooterLoading(null));
			return;
		}

		void runSelfEmploymentCheck({mode: 'permissions'}).finally(() => setMyTaxFooterLoading(null));
	};

	const handleMyTaxNotSelfEmployedCheckInn = () => {
		setStep(RegistrationStep.Inn);
	};

	const handleFnsPendingContinue = () => {
		if (workStatus) {
			const pendingState =
				step === RegistrationStep.PartnerAgreementPending ? 'pending_alfa' : 'pending';

			setWorkStatusConnections((current) => ({...current, [workStatus]: pendingState}));
		}

		setWorkStatus(null);

		if (WORK_STATUS_STEP_ENABLED) {
			setStep(RegistrationStep.WorkStatus);
		} else {
			handleClose();
		}
	};

	/**
	 * Пока открыт iframe анкеты Альфа — полим GET /employment.
	 * `agreement.signed` / `pending` / ФНС / «не СМЗ» → закрываем iframe и идём дальше.
	 */
	useEffect(() => {
		if (!partnerAgreementLink) {
			return;
		}

		let cancelled = false;
		let timeoutId: ReturnType<typeof setTimeout> | undefined;

		const closeFrameAndGo = (data: EmploymentsResponse, nextStep: RegistrationStep) => {
			openedAgreementFromWorkStatusRef.current = false;
			agreementLinkRef.current = null;
			setPartnerAgreementLink(null);

			if (nextStep === RegistrationStep.WorkStatus) {
				setWorkStatusConnections(mapEmploymentsToWorkStatusConnections(data));
				setWorkStatus(null);

				if (WORK_STATUS_STEP_ENABLED) {
					setStep(RegistrationStep.WorkStatus);
				} else {
					handleClose();
				}

				return;
			}

			setStep(nextStep);
		};

		const poll = async () => {
			try {
				const {data} = await fetchEmployment();

				if (cancelled) {
					return;
				}

				queryClient.setQueryData(['employment'], data);

				const nextStep = resolvePartnerAgreementPollStep(data, partnerProvider);

				if (nextStep) {
					closeFrameAndGo(data, nextStep);
					return;
				}
			} catch {
				/* сеть/бэк — пробуем дальше, пока iframe открыт */
			}

			if (cancelled) {
				return;
			}

			timeoutId = setTimeout(() => void poll(), PARTNER_AGREEMENT_POLL_INTERVAL_MS);
		};

		void poll();

		return () => {
			cancelled = true;
			clearTimeout(timeoutId);
		};
	}, [partnerAgreementLink, partnerProvider, queryClient, setStep, setWorkStatus, setWorkStatusConnections]);

	/**
	 * «Секундочку..» после закрытия iframe: до 10 раз с интервалом 7 с опрашиваем GET /employment.
	 * signed + just_sent_inn / pending (не BIND_REQUEST, только Альфа) → «Ждём ответ ФНС»;
	 * signed + pending + BIND_REQUEST → «Нужен доступ в мой налог»;
	 * signed + not_selfemployed → «Что-то не так»;
	 * agreement.sent → список с ошибкой «Подпишите соглашение».
	 */
	useEffect(() => {
		if (step !== RegistrationStep.PartnerAgreementChecking || partnerAgreementLink) {
			return;
		}

		let cancelled = false;
		let timeoutId: ReturnType<typeof setTimeout> | undefined;
		let attempt = 0;

		const goToSignAgreementList = () => {
			markAlfaAgreementFrameOpened(partnerProvider);
			agreementLinkRef.current = null;
			setWorkStatus(null);

			if (WORK_STATUS_STEP_ENABLED) {
				setStep(RegistrationStep.WorkStatus);
			} else {
				handleClose();
			}
		};

		const poll = async () => {
			attempt += 1;

			try {
				const {data} = await fetchEmployment();

				if (cancelled) {
					return;
				}

				queryClient.setQueryData(['employment'], data);

				const nextStep = resolvePartnerAgreementPollStep(data, partnerProvider);

				if (nextStep) {
					agreementLinkRef.current = null;

					if (nextStep === RegistrationStep.WorkStatus) {
						setWorkStatusConnections(mapEmploymentsToWorkStatusConnections(data));
						setWorkStatus(null);

						if (WORK_STATUS_STEP_ENABLED) {
							setStep(RegistrationStep.WorkStatus);
						} else {
							handleClose();
						}

						return;
					}

					setStep(nextStep);
					return;
				}

			} catch {
				/* сеть/бэк — считаем попытку, пробуем дальше */
			}

			if (cancelled) {
				return;
			}

			if (attempt >= PARTNER_AGREEMENT_POLL_ATTEMPTS) {
				goToSignAgreementList();
				return;
			}

			timeoutId = setTimeout(() => void poll(), PARTNER_AGREEMENT_POLL_INTERVAL_MS);
		};

		void poll();

		return () => {
			cancelled = true;
			clearTimeout(timeoutId);
		};
	}, [partnerAgreementLink, partnerProvider, queryClient, setStep, setWorkStatus, setWorkStatusConnections, step, workStatus]);

	return {
		bindSelfEmploymentPartnerAfterInn,
		completeStatusConnection,
		exitStatusConnectionToWorkStatus,
		goAfterSnilsStep,
		goAfterStatusDocuments,
		goAfterTaxStep,
		handleAccessGranted,
		handleAlfaStatusConfirm,
		handleAlfaStatusConfirmCancel,
		handleFnsPendingContinue,
		handleGoToMyTax,
		handleMyTaxAccessErrorRetry,
		handleMyTaxIntroProceed,
		handleMyTaxIntroSecondary,
		handleMyTaxLoadErrorRetry,
		handleMyTaxNameMismatchConfirm,
		handleMyTaxNotSelfEmployedCheckInn,
		handlePartnerAgreementClose,
		handleFreeSmzSelect,
		handleWorkStatusSelect,
		openPartnerAgreement,
		isAlfaStatusConfirmOpen,
		partnerAgreementLink,
	};
};

export type StatusConnectionFlow = ReturnType<typeof useStatusConnectionFlow>;
